import React, { useCallback, useEffect, useState } from 'react'
import { useStateContext } from '../../../context/ContextProvider'
import axiosClient from '../../../axios-client'
import { Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement,
    Title, Tooltip, Legend, ArcElement,
} from 'chart.js';
import {
    Avatar, Badge, Box, Button, CloseButton, Collapse, Divider,
    Flex, Grid, Icon, Image, Progress, Select, SimpleGrid, Spinner,
    Tab, TabList, TabPanel, TabPanels, Tabs, Text,
    Tooltip as ChakraTooltip, useColorModeValue, useToast,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, IconButton,
    Popover, PopoverTrigger, PopoverContent, PopoverBody,
} from '@chakra-ui/react';
import { MdAddShoppingCart, MdOutlineBarChart, MdOutlineRemoveShoppingCart, MdTrendingUp, MdTrendingDown, MdAttachMoney, MdOutlineCancel, MdLocalShipping, MdInventory, MdCheckCircle, MdHourglassEmpty, MdPauseCircle, MdPayments, MdMoneyOff } from 'react-icons/md';
import { TbTruckDelivery, TbTruckReturn } from 'react-icons/tb';
import { LuPackageX, LuPackageCheck, LuPackage } from 'react-icons/lu';
import { FiRefreshCw, FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';
import { BsGraphUp, BsGraphDown, BsPercent, BsCurrencyExchange, BsCashCoin } from 'react-icons/bs';
import { RiMoneyDollarCircleLine, RiAdvertisementLine } from 'react-icons/ri';
import { HiOutlineShoppingBag } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import LineChart from '../../../components/Charts/LineChart';
import { IoIosArrowForward } from "react-icons/io";
import { useNavigate } from 'react-router-dom';
import { DateRange } from 'react-date-range';
import format from 'date-fns/format';
import fr from 'date-fns/locale/fr';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { AsyncPaginate } from 'react-select-async-paginate';
import FormattedNumber, { formatNumber } from '../../../components/FormattedNumber';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

/* ─── Global CSS injected once ─────────────────────────────────────────────── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.stats-root { font-family: 'Inter', sans-serif; }

@keyframes fadeUp   { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
@keyframes scaleIn  { from { opacity:0; transform:scale(.93);        } to { opacity:1; transform:scale(1);    } }
@keyframes float    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }

.fade-up  { animation: fadeUp  .45s ease both; }
.scale-in { animation: scaleIn .35s ease both; }

.glass-card {
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255,255,255,.18);
}

.gradient-text {
  background: linear-gradient(135deg, #7B5EA7 0%, #6DD5FA 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.metric-card {
  transition: transform .22s ease, box-shadow .22s ease;
  cursor:default;
}
.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(123,94,167,.15);
}

.product-card {
  transition: all .2s ease;
  cursor: pointer;
}
.product-card:hover  { transform:translateY(-4px); }
.product-card.active { border-color:#7B5EA7 !important; }

.tab-active {
  background: linear-gradient(135deg,#7B5EA7,#5A3B8A) !important;
  color:white !important;
}

.floating-icon { animation: float 3s ease-in-out infinite; }

/* date-range overrides */
.rdrCalendarWrapper { border-radius:16px; overflow:hidden; }
.rdrDefinedRangesWrapper { display:none; }
`;

/* ─── External Sub-components ─────────────────────────────────────────────────────────  */

const GlassMetricCard = ({ label, value, sub, icon: IconComp, gradient, badge, tooltip, delay = 0, isDark }) => {
    const card = (
        <Box
            className="metric-card fade-up glass-card"
            style={{ animationDelay: `${delay}ms` }}
            bg={isDark ? 'rgba(26,32,44,0.75)' : 'rgba(255,255,255,0.82)'}
            borderRadius="16px"
            p={4}
            position="relative"
            overflow="hidden"
            h="100%"
        >
            <Box
                position="absolute" top="-20px" right="-20px"
                w="60px" h="60px" borderRadius="50%"
                bg={gradient} opacity={0.2} filter="blur(14px)"
            />
            <Flex justify="space-between" align="flex-start" h="100%">
                <Flex direction="column" gap={1} flex={1}>
                    <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="1px"
                        color={isDark ? 'whiteAlpha.500' : 'gray.400'}>
                        {label}
                    </Text>
                    <Text fontSize="20px" fontWeight="800" lineHeight={1.1}
                        color={isDark ? 'whiteAlpha.900' : 'gray.800'}>
                        {value ?? <Spinner size="sm" />}
                    </Text>
                    {sub && (
                        <Text fontSize="10px" color={isDark ? 'whiteAlpha.500' : 'gray.400'} mt={0.5}>
                            {sub}
                        </Text>
                    )}
                    {badge && (
                        <Box mt={2} px={2} py={0.5} borderRadius="6px" bg={gradient} display="inline-block" w="fit-content">
                            <Text fontSize="9px" fontWeight="700" color="white">{badge}</Text>
                        </Box>
                    )}
                </Flex>
                <Box
                    borderRadius="12px" p={2.5} bg={gradient}
                    display="flex" alignItems="center" justifyContent="center"
                    className="floating-icon" ml={2} flexShrink={0}
                >
                    <IconComp size={18} color="white" />
                </Box>
            </Flex>
        </Box>
    );
    if (tooltip) return (
        <ChakraTooltip label={tooltip} placement="top" hasArrow bg="gray.800" color="white" fontSize="xs" maxW="220px" px={3} py={2} borderRadius="lg">
            {card}
        </ChakraTooltip>
    );
    return card;
};

const RateGauge = ({ label, value, colorStart, colorEnd, delay = 0, isDark }) => {
    const pct = Math.min(Math.max(parseFloat(value) || 0, 0), 100);
    return (
        <Box
            className="fade-up glass-card"
            style={{ animationDelay: `${delay}ms` }}
            bg={isDark ? 'rgba(26,32,44,0.75)' : 'rgba(255,255,255,0.82)'}
            borderRadius="16px" p={4}
            transition="all .2s" _hover={{ transform: 'translateY(-2px)', boxShadow: '0 4px 14px rgba(0,0,0,.08)' }}
        >
            <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="11px" fontWeight="700" color={isDark ? 'whiteAlpha.600' : 'gray.500'} textTransform="uppercase" letterSpacing="1px">
                    {label}
                </Text>
                <Text fontSize="16px" fontWeight="800"
                    style={{ background: `linear-gradient(135deg,${colorStart},${colorEnd})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {pct.toFixed(1)}%
                </Text>
            </Flex>
            <Box bg={isDark ? 'whiteAlpha.100' : 'gray.100'} borderRadius="99px" h="6px" overflow="hidden">
                <Box
                    h="100%" borderRadius="99px"
                    style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg,${colorStart},${colorEnd})`,
                        boxShadow: `0 0 8px ${colorStart}80`,
                        transition: 'width 1.2s cubic-bezier(.4,0,.2,1)',
                    }}
                />
            </Box>
        </Box>
    );
};

const SectionBadge = ({ children, icon: I }) => (
    <Flex align="center" gap={2} mb={4}>
        <Box p={1.5} borderRadius="8px" bg="linear-gradient(135deg,#7B5EA7,#5A3B8A)" display="flex">
            <I size={14} color="white" />
        </Box>
        <Text fontSize="15px" fontWeight="800" letterSpacing="-0.3px">{children}</Text>
    </Flex>
);

const RankTable = ({ data, cols, isDark }) => {
    const { t } = useTranslation();
    const rankColors = ['linear-gradient(135deg,#F6D365,#FDA085)', 'linear-gradient(135deg,#a8a8a8,#d0d0d0)', 'linear-gradient(135deg,#f7971e,#ffd200)'];
    return (
        <TableContainer>
            <Table size="sm" variant="unstyled">
                <Thead>
                    <Tr>
                        <Th fontSize="9px" textTransform="uppercase" letterSpacing="1px" color="gray.400">#</Th>
                        <Th fontSize="9px" textTransform="uppercase" letterSpacing="1px" color="gray.400">{t('Produit')}</Th>
                        {cols.map(c => <Th key={c.key} isNumeric fontSize="9px" textTransform="uppercase" letterSpacing="1px" color="gray.400">{c.label}</Th>)}
                    </Tr>
                </Thead>
                <Tbody>
                    {(data || []).slice(0, 10).map((row, i) => (
                        <Tr key={row.product_id}
                            borderRadius="8px"
                            _hover={{ bg: isDark ? 'whiteAlpha.50' : 'purple.50' }}
                            transition="all .15s"
                        >
                            <Td py={2}>
                                <Box
                                    w="20px" h="20px" borderRadius="50%" display="flex" alignItems="center" justifyContent="center"
                                    bg={rankColors[i] || (isDark ? 'whiteAlpha.100' : 'gray.100')}
                                >
                                    <Text fontSize="10px" fontWeight="800" color={i < 3 ? 'white' : isDark ? 'whiteAlpha.700' : 'gray.600'}>{i + 1}</Text>
                                </Box>
                            </Td>
                            <Td py={2} fontWeight="600" fontSize="12px" maxW="150px" isTruncated>{row.product_title}</Td>
                            {cols.map(c => (
                                <Td key={c.key} isNumeric py={2} fontSize="12px" fontWeight="700" color={c.color ? c.color(row[c.key]) : 'inherit'}>
                                    {c.format ? c.format(row[c.key]) : <FormattedNumber value={row[c.key]} />}
                                </Td>
                            ))}
                        </Tr>
                    ))}
                    {(!data || !data.length) && (
                        <Tr><Td colSpan={2 + cols.length} textAlign="center" py={8} color="gray.400" fontSize="12px">{t('Aucune donnée disponible')}</Td></Tr>
                    )}
                </Tbody>
            </Table>
        </TableContainer>
    );
};

const Section = ({ children, delay = 0, isDark }) => (
    <Box
        className="fade-up glass-card"
        style={{ animationDelay: `${delay}ms` }}
        bg={isDark ? 'rgba(26,32,44,0.82)' : 'rgba(255,255,255,0.88)'}
        borderRadius="20px"
        p={{ base: 3, md: 5 }}
        mb={5}
        border={`1px solid ${isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.07)'}`}
        boxShadow={isDark ? '0 8px 32px rgba(0,0,0,.4)' : '0 4px 20px rgba(107,99,255,.06)'}
    >
        {children}
    </Box>
);

/* ─── Main Component ─────────────────────────────────────────────────────────  */
function Statistics() {
    const { user, setUser, setToken, currentBusiness } = useStateContext();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();
    const isDark = useColorModeValue(false, true);

    /* Order counts */
    const [counts, setCounts] = useState({});
    /* Finance */

    const [chargesStats, setChargesStats] = useState(null);
    const [chargesData, setChargesData] = useState(null);
    const [costsData, setCostsData] = useState(null);
    const [tauxData, setTauxData] = useState(null);
    /* Products */
    const [products, setProducts] = useState([]);
    const [productPage, setProductPage] = useState(0);
    const PER_PAGE = 6;
    /* Personnel */
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userStatsOpen, setUserStatsOpen] = useState(false);
    const [userStatsSuivi, setUserStatsSuivi] = useState(null);
    const [userStatsStock, setUserStatsStock] = useState(null);
    /* Breakdowns */
    const [breakdownData, setBreakdownData] = useState(null);
    const [rankingsData, setRankingsData] = useState(null);
    /* Filters */
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedProductObj, setSelectedProductObj] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectionRange, setSelectionRange] = useState({ startDate: new Date(), endDate: new Date(), key: 'selection' });
    /* Chart */
    const [lineChartData, setLineChartData] = useState(null);
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    /* Active tab states */
    const [breakdownTab, setBreakdownTab] = useState(0);
    const [rankingTab, setRankingTab] = useState(0);


    /* ── Fetch helpers ── */
    const qp = (extra = {}) => {
        const base = { client: user?.id, product: selectedProduct, dateStart, dateEnd, userRole: user?.role || user?.role_client, business: currentBusiness?.id, convert_currency: true };
        return new URLSearchParams({ ...base, ...extra }).toString();
    };

    const disabledClient = () => {
        setUser({}); setToken(null);
        ['tokenExpirationTime', 'refreshTimeAdmin', 'role'].forEach(k => localStorage.removeItem(k));
        if (!toast.isActive('acDisabled'))
            toast({ id: 'acDisabled', description: t('le compte est désactivé'), status: 'warning', duration: 2000, isClosable: true, position: 'bottom-right' });
    };

    const fetchAll = useCallback(async () => {
        if (!user?.id || !currentBusiness?.id) return;

        /* Counts */
        axiosClient.get(`/commandes/getCommandesCount/?${qp()}`)
            .then(r => { if (r.data?.error) return disabledClient(); setCounts(r.data); })
            .catch(() => { });



        /* Charges total */
        axiosClient.get(`/charges/getStatsCharges/?${qp()}`)
            .then(r => setChargesStats(parseFloat(r.data) || 0))
            .catch(() => { });

        /* Taux */
        axiosClient.get(`/commandes/getTaux/?${qp()}`)
            .then(r => setTauxData(r.data)).catch(() => { });

        /* Costs */
        axiosClient.get(`/commandes/getCosts/?${qp()}`)
            .then(r => {
                console.log("r.data", r.data)
                setCostsData(r.data)
            }).catch(() => { });

        /* Charges breakdown */
        axiosClient.get(`/charges/getCharges/?${qp()}`)
            .then(r => setChargesData(r.data)).catch(() => { });

        /* Line chart */
        Promise.all([
            axiosClient.get(`/commandes/getRevenusByMonth/?${qp({ year })}`).catch(() => ({ data: [] })),
            axiosClient.get(`/charges/getChargesByMonth/?${qp({ year })}`).catch(() => ({ data: [] }))
        ]).then(([revRes, charRes]) => {
            const revenus = Array.isArray(revRes.data) ? revRes.data : [];
            const charges = Array.isArray(charRes.data) ? charRes.data : [];

            if (revenus.length > 0 || charges.length > 0) {
                setLineChartData([
                    { name: t('Revenus'), data: revenus },
                    { name: t('Charges'), data: charges },
                ]);
            } else {
                setLineChartData(null);
            }
        });

        /* Products */
        axiosClient.get(`/products/getAllProducts/?id_user=${user?.id}&page_size=100&page=1&search_term=&userRole=${user?.role || user?.role_client}&business=${currentBusiness?.id}`)
            .then(r => setProducts(r.data?.results || r.data || [])).catch(() => { });

        /* Users */
        axiosClient.get(`/clients/getByAdminClient/?${qp()}`)
            .then(r => setUsers(r.data?.results || r.data || [])).catch(() => { });

        /* Breakdown */
        axiosClient.get(`/commandes/getBreakdownStats/?${qp()}`)
            .then(r => setBreakdownData(r.data)).catch(() => { });

        /* Rankings */
        axiosClient.get(`/commandes/getProductRankings/?${qp()}`)
            .then(r => setRankingsData(r.data)).catch(() => { });

    }, [user?.id, currentBusiness?.id, selectedProduct, dateStart, dateEnd, year]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    /* ── Filter handlers ── */
    const handleDateSelect = ({ selection }) => {
        setSelectionRange(selection);
        setDateStart(format(selection.startDate, 'yyyy-MM-dd'));
        setDateEnd(format(selection.endDate, 'yyyy-MM-dd'));
    };

    const handleReset = () => {
        setDateStart(''); setDateEnd('');
        setSelectionRange({ startDate: new Date(), endDate: new Date(), key: 'selection' });
        setSelectedProduct(''); setSelectedProductObj(null);
    };

    const loadProductOptions = async (search, _, { page }) => {
        const r = await axiosClient.get(`/products/getAllProducts/?id_user=${user?.id}&page_size=15&page=${page}&search_term=${search || ''}&userRole=${user?.role || user?.role_client}&business=${currentBusiness?.id}`);
        const data = r.data?.results || r.data || [];
        return {
            options: [...(page === 1 ? [{ value: '', label: t('Tous les produits') }] : []), ...data.map(p => ({ value: String(p.id), label: p.title }))],
            hasMore: !!r.data?.next,
            additional: { page: page + 1 },
        };
    };


    const isDualCur = currentBusiness?.order_currency && currentBusiness?.currency && currentBusiness.order_currency !== currentBusiness.currency;
    const cur = currentBusiness?.currency || '';
    const orderCur = currentBusiness?.order_currency || '';

    /* ── Charges chart data ── */
    const chargeKeys = ['delivery', 'marketing', 'salaires', 'stock', 'shipping', 'storage', 'fullfilment', 'service_cod', 'packaging', 'maintenance', 'insurance', 'taxes', 'material', 'subscription', 'call_center', 'rent', 'freelancer', 'autre'];
    const chargeLabels = [t('Livraison'), t('Marketing'), t('Salaires'), t('Stock'), t('Expédition'), t('Stockage'), t('Fulfillment'), t('Service COD'), t('Emballage'), t('Maintenance'), t('Assurance'), t('Impôts'), t('Matériel'), t('Abonnement'), t("Centre d'appels"), t('Loyer'), t('Freelancer'), t('Autre')];
    const chargeVals = chargeKeys.map(k => chargesData?.[k] || 0);
    const chargeColors = ["#6C63FF", "#FF6584", "#43E97B", "#F7971E", "#4FACFE", "#FA709A", "#30CFD0", "#F77062", "#C471ED", "#FFD700", "#0CEBEB", "#FF4E50", "#F9D423", "#56AB2F", "#A18CD1", "#FF6B6B", "#48C6EF", "#A8EDEA"];
    const hasCharges = chargeVals.some(v => v > 0);
    const totalCharges = chargeVals.reduce((a, b) => a + b, 0);

    const filteredLabels = chargeLabels.filter((_, i) => chargeVals[i] > 0);
    const filteredVals = chargeVals.filter(v => v > 0);
    const filteredColors = chargeColors.filter((_, i) => chargeVals[i] > 0);

    /* Donut */
    const donutData = {
        labels: filteredLabels,
        datasets: [{ data: filteredVals, backgroundColor: filteredColors, hoverOffset: 12, borderWidth: 0 }]
    };
    const donutOptions = {
        maintainAspectRatio: false,
        cutout: '72%',
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => `${ctx.label}: ${formatNumber(ctx.parsed)} ${cur}` } }
        }
    };

    /* Bar chart (expenses) */
    const expBarData = {
        labels: filteredLabels,
        datasets: [{
            data: filteredVals,
            backgroundColor: filteredColors.map(c => c + 'BB'),
            borderColor: filteredColors,
            borderWidth: 2,
            borderRadius: 6,
        }]
    };
    const expBarOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => `${formatNumber(ctx.parsed.y)} ${cur}` } } },
        scales: {
            x: { ticks: { font: { size: 9 }, maxRotation: 45, color: isDark ? '#718096' : '#718096' }, grid: { display: false } },
            y: { ticks: { color: isDark ? '#718096' : '#718096', callback: v => formatNumber(v) }, grid: { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' } }
        }
    };

    /* Status bar */
    const statusData = {
        labels: [t('Nouveau'), t('Encours'), t('Confirmée'), t('Livrée'), t('Retournée'), t('Annulée')],
        datasets: [{
            data: [counts.count_new, counts.count_pending, counts.count_confirmed, counts.count_livre, counts.count_retourne, counts.count_annule],
            backgroundColor: ['#4FACFE', '#FFD700', '#43E97B', '#6C63FF', '#FF9F40', '#FF6384'],
            borderRadius: 6, borderWidth: 0,
        }]
    };
    const statusOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(0,0,0,0.8)',
                padding: 12,
                cornerRadius: 8,
                titleFont: { family: 'Inter', size: 14 },
                bodyFont: { family: 'Inter', size: 13 },
                callbacks: { label: ctx => ` ${formatNumber(ctx.parsed.y, false)} ${t('Commandes')}` }
            }
        },
        scales: {
            y: { ticks: { color: isDark ? '#718096' : '#718096', callback: v => formatNumber(v, false) }, grid: { color: isDark ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.04)' } },
            x: { ticks: { color: isDark ? '#718096' : '#718096', font: { family: 'Inter', weight: '500' } }, grid: { display: false } }
        }
    };

    /* Line chart */
    const lineOptions = {
        chart: { toolbar: { show: false } },
        tooltip: { theme: 'dark', y: { formatter: v => formatNumber(v) } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        xaxis: {
            type: "category",
            categories: [t('Jan'), t('Fév'), t('Mar'), t('Avr'), t('Mai'), t('Jui'), t('Juil'), t('Aou'), t('Sep'), t('Oct'), t('Nov'), t('Dec')],
            labels: { style: { colors: isDark ? '#718096' : '#718096', fontSize: '11px', fontFamily: 'Inter' } },
            axisBorder: { show: false }, axisTicks: { show: false }
        },
        yaxis: { labels: { style: { colors: isDark ? '#718096' : '#718096', fontSize: '11px', fontFamily: 'Inter' }, formatter: v => formatNumber(v) } },
        legend: { show: false },
        grid: { strokeDashArray: 5, borderColor: isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.07)' },
        fill: { type: 'gradient', gradient: { shade: 'dark', type: 'vertical', opacityFrom: 0.7, opacityTo: 0 }, colors: ['#6C63FF', '#FF6584'] },
        colors: ['#6C63FF', '#FF6584'],
    };

    /* Paginated products */
    const totalPages = Math.ceil(products.length / PER_PAGE);
    const visibleProducts = products.slice(productPage * PER_PAGE, (productPage + 1) * PER_PAGE);

    /* Center-text plugin for donut */


    /* ─── BG & surface colors ─── */
    const pageBg = isDark
        ? 'linear-gradient(135deg, #0F0C29 0%, #302B63 50%, #24243e 100%)'
        : 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 50%, #f0f4ff 100%)';
    const borderCol = isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.07)';

    /* ─── Render ─── */
    return (
        <>
            <style>{GLOBAL_CSS}</style>
            <Box
                className="stats-root"
                minH="100vh"
                bg={pageBg}
                p={{ base: 3, md: 5 }}
                position="relative"
            >
                {/* Ambient blobs */}
                <Box position="fixed" top="-100px" left="-100px" w="400px" h="400px" borderRadius="50%"
                    bg="radial-gradient(circle,rgba(108,99,255,.25) 0%,transparent 70%)" pointerEvents="none" zIndex={0} />
                <Box position="fixed" bottom="-80px" right="-80px" w="350px" h="350px" borderRadius="50%"
                    bg="radial-gradient(circle,rgba(255,101,132,.18) 0%,transparent 70%)" pointerEvents="none" zIndex={0} />

                <Box position="relative" zIndex={1}>
                    {/* ─── TOP HEADER ─── */}
                    <Flex
                        className="fade-up"
                        position="relative"
                        zIndex={10}
                        justify="space-between"
                        align={{ base: 'flex-start', md: 'center' }}
                        direction={{ base: 'column', md: 'row' }}
                        gap={4}
                        mb={6}
                    >
                        <Box>
                            <Text fontSize={{ base: '22px', md: '28px' }} fontWeight="900" letterSpacing="-1px"
                                className="gradient-text">
                                {t('Statistiques')}
                            </Text>
                            <Text fontSize="12px" color={isDark ? 'whiteAlpha.500' : 'gray.400'} mt={0.5}>
                                {currentBusiness?.name || ''} · {dateStart ? `${dateStart} → ${dateEnd}` : t('Toutes les périodes')}
                            </Text>
                        </Box>

                        <Flex gap={2} flexWrap="wrap" align="center">
                            {/* Date picker */}
                            <Popover placement="bottom-end">
                                {({ onClose }) => (
                                    <>
                                        <PopoverTrigger>
                                            <Box
                                                as="button"
                                                bg={isDark ? 'whiteAlpha.100' : 'white'}
                                                border={`1px solid ${borderCol}`}
                                                borderRadius="10px"
                                                px={3} py="7px"
                                                display="flex" alignItems="center" gap={2}
                                                fontSize="12px" fontWeight="600"
                                                color={isDark ? 'whiteAlpha.800' : 'gray.700'}
                                                transition="all .15s"
                                                _hover={{ bg: isDark ? 'whiteAlpha.200' : 'purple.50', borderColor: 'purple.300' }}
                                            >
                                                <FiCalendar size={14} />
                                                {dateStart ? `${dateStart} → ${dateEnd}` : t('Filtrer par date')}
                                            </Box>
                                        </PopoverTrigger>
                                        <PopoverContent w="auto" border="none" boxShadow="0 20px 60px rgba(0,0,0,.2)" borderRadius="16px" zIndex={9999} bg={isDark ? '#1A202C' : 'white'} _focus={{ outline: 'none' }}>
                                            <PopoverBody p={0}>
                                                <DateRange
                                                    ranges={[selectionRange]}
                                                    onChange={handleDateSelect}
                                                    locale={i18n.language === 'en' ? undefined : fr}
                                                    moveRangeOnFirstSelection={false}
                                                />
                                                <Flex justify="flex-end" px={3} pb={3} gap={2}>
                                                    <Box as="button" px={3} py={1.5} borderRadius="6px" fontSize="11px" fontWeight="600"
                                                        color="gray.500" onClick={onClose}>{t('Fermer')}</Box>
                                                    <Box as="button" px={3} py={1.5} borderRadius="6px" fontSize="11px" fontWeight="700"
                                                        bg="linear-gradient(135deg,#7B5EA7,#5A3B8A)" color="white"
                                                        onClick={onClose}>
                                                        {t('Appliquer')}
                                                    </Box>
                                                </Flex>
                                            </PopoverBody>
                                        </PopoverContent>
                                    </>
                                )}
                            </Popover>

                            {/* Product filter */}
                            <Box minW="160px">
                                <AsyncPaginate
                                    value={selectedProductObj}
                                    loadOptions={loadProductOptions}
                                    onChange={opt => { setSelectedProductObj(opt); setSelectedProduct(opt?.value || ''); }}
                                    additional={{ page: 1 }}
                                    placeholder={`${t('Produit')}...`}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={{
                                        control: b => ({
                                            ...b, minHeight: '32px', fontSize: '12px', fontWeight: 600,
                                            borderRadius: '10px', borderColor: borderCol,
                                            background: isDark ? 'rgba(255,255,255,.08)' : 'white',
                                            color: isDark ? 'white' : '#2D3748',
                                        }),
                                        menuPortal: b => ({ ...b, zIndex: 9999 }),
                                        singleValue: b => ({ ...b, color: isDark ? 'white' : '#2D3748' }),
                                        input: b => ({ ...b, color: isDark ? 'white' : '#2D3748' }),
                                    }}
                                />
                            </Box>

                            {/* Reset */}
                            <Box as="button" onClick={handleReset}
                                bg={isDark ? 'whiteAlpha.100' : 'white'} border={`1px solid ${borderCol}`}
                                borderRadius="10px" px={3} py="7px"
                                display="flex" alignItems="center" gap={1.5}
                                fontSize="12px" fontWeight="600" color={isDark ? 'whiteAlpha.700' : 'gray.500'}
                                transition="all .15s" _hover={{ borderColor: 'purple.300', color: 'purple.500' }}>
                                <FiRefreshCw size={12} />
                                {t('Réinitialiser')}
                            </Box>
                        </Flex>
                    </Flex>

                    {/* ─── HERO KPIs ─── */}
                    <SimpleGrid columns={{ base: 2, sm: 2, md: 3, lg: 5 }} gap={3} mb={5}>
                        {[
                            { label: t('Total des commandes'), value: <FormattedNumber value={counts.count_total} />, icon: HiOutlineShoppingBag, gradient: 'linear-gradient(135deg,#6C63FF,#3D35B1)' },
                            { label: t("Chiffre d'affaire"), value: <FormattedNumber value={costsData?.revenue} suffix={` ${cur}`} />, sub: isDualCur ? <FormattedNumber value={costsData?.revenue_raw} suffix={` ${orderCur}`} /> : null, icon: BsCurrencyExchange, gradient: 'linear-gradient(135deg,#43E97B,#38F9D7)' },
                            { label: t('Profit net'), value: <FormattedNumber value={costsData?.net_profit} suffix={` ${cur}`} />, icon: (costsData?.net_profit || 0) >= 0 ? BsGraphUp : BsGraphDown, gradient: (costsData?.net_profit || 0) >= 0 ? 'linear-gradient(135deg,#56AB2F,#A8E063)' : 'linear-gradient(135deg,#FF416C,#FF4B2B)' },
                            { label: t('Dépense publicitaire'), value: <FormattedNumber value={costsData?.total_ad_spend} suffix={` ${cur}`} />, icon: RiAdvertisementLine, gradient: 'linear-gradient(135deg,#FA709A,#FEE140)' },
                            { label: t('ROAS'), value: <FormattedNumber value={costsData?.roas} />, icon: MdTrendingUp, gradient: 'linear-gradient(135deg,#4FACFE,#00F2FE)', badge: parseFloat(costsData?.roas) >= 2 ? `✓ ${t('Bon')}` : null },
                        ].map((c, i) => (
                            <GlassMetricCard key={i} delay={i * 50} isDark={isDark} {...c} />
                        ))}
                    </SimpleGrid>

                    {/* ─── PRODUCT LIST ─── */}
                    {products.length > 0 && (
                        <Section delay={150} isDark={isDark}>
                            <Flex justify="space-between" align="center" mb={3}>
                                <Text fontWeight="800" fontSize="13px">{t('Liste des produits')}</Text>
                                <Flex align="center" gap={2}>
                                    <IconButton icon={<FiChevronLeft />} size="xs" variant="ghost" isDisabled={productPage === 0}
                                        onClick={() => setProductPage(p => p - 1)} borderRadius="6px" />
                                    <Text fontSize="11px" color="gray.400" fontWeight="600">{productPage + 1}/{Math.max(totalPages, 1)}</Text>
                                    <IconButton icon={<FiChevronRight />} size="xs" variant="ghost" isDisabled={productPage >= totalPages - 1}
                                        onClick={() => setProductPage(p => p + 1)} borderRadius="6px" />
                                    <Box as="button" onClick={() => navigate('/products')}
                                        display="flex" alignItems="center" gap={1}
                                        fontSize="11px" fontWeight="700" color="purple.500"
                                        _hover={{ textDecoration: 'underline' }}>
                                        {t('Voir tout')} <IoIosArrowForward />
                                    </Box>
                                </Flex>
                            </Flex>
                            <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} gap={2}>
                                {visibleProducts.map(product => (
                                    <Box
                                        key={product.id}
                                        className={`product-card${selectedProduct === String(product.id) ? ' active' : ''}`}
                                        borderRadius="12px"
                                        border={`2px solid ${selectedProduct === String(product.id) ? '#7B5EA7' : borderCol}`}
                                        bg={selectedProduct === String(product.id)
                                            ? isDark ? 'rgba(123,94,167,.2)' : 'rgba(123,94,167,.07)'
                                            : isDark ? 'rgba(255,255,255,.04)' : 'rgba(255,255,255,.9)'}
                                        p={2}
                                        display="flex" flexDirection="column" alignItems="center"
                                        onClick={() => {
                                            if (selectedProduct === String(product.id)) { setSelectedProduct(''); setSelectedProductObj(null); }
                                            else { setSelectedProduct(String(product.id)); setSelectedProductObj({ value: String(product.id), label: product.title }); }
                                        }}
                                    >
                                        {product?.images?.[0]?.image
                                            ? <Image src={product.images[0].image} h="50px" w="50px" objectFit="contain" borderRadius="8px" mb={2} />
                                            : <Box h="50px" w="50px" bg={isDark ? 'whiteAlpha.100' : 'gray.100'} borderRadius="8px" mb={2}
                                                display="flex" alignItems="center" justifyContent="center">
                                                <HiOutlineShoppingBag size={20} color="#A0AEC0" />
                                            </Box>}
                                        <Text fontSize="10px" fontWeight="700" textAlign="center" noOfLines={2}
                                            color={isDark ? 'whiteAlpha.800' : 'gray.700'}>{product.title}</Text>
                                    </Box>
                                ))}
                            </SimpleGrid>
                        </Section>
                    )}

                    {/* ─── ORDER METRICS ─── */}
                    <Section delay={200} isDark={isDark}>
                        <SectionBadge icon={HiOutlineShoppingBag}>{t('Métriques des commandes')}</SectionBadge>
                        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} gap={3} mb={4}>
                            {[
                                { label: t('Nouvelles'), value: <FormattedNumber value={counts.count_new} />, icon: MdAddShoppingCart, gradient: 'linear-gradient(135deg,#4FACFE,#00F2FE)', delay: 0 },
                                { label: t('En attente'), value: <FormattedNumber value={counts.count_pending} />, icon: MdHourglassEmpty, gradient: 'linear-gradient(135deg,#FFD700,#FFA500)', delay: 40 },
                                { label: t('Confirmées'), value: <FormattedNumber value={counts.count_confirmed} />, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#43E97B,#38F9D7)', delay: 80 },
                                { label: t('Annulées'), value: <FormattedNumber value={counts.count_annule} />, icon: MdOutlineCancel, gradient: 'linear-gradient(135deg,#FF6B6B,#FF416C)', delay: 120 },
                                { label: t('En att. traitement'), value: <FormattedNumber value={counts.count_pending_fulfillment} />, icon: MdPauseCircle, gradient: 'linear-gradient(135deg,#F7971E,#FFD200)', delay: 160, tooltip: t('Confirmées sans statut stock ni livraison') },
                                { label: t('Emballées'), value: <FormattedNumber value={counts.count_packed} />, icon: LuPackageCheck, gradient: 'linear-gradient(135deg,#30CFD0,#330867)', delay: 200 },
                            ].map((c, i) => <GlassMetricCard key={i} isDark={isDark} {...c} />)}
                        </SimpleGrid>
                        <SimpleGrid columns={{ base: 2, sm: 3, md: 3, lg: 6 }} gap={3} mb={4}>
                            {[
                                { label: t('Livraison en cours'), value: <FormattedNumber value={counts.count_pending_delivery} />, icon: TbTruckDelivery, gradient: 'linear-gradient(135deg,#C471ED,#12C2E9)', delay: 0 },
                                { label: t('Livrées'), value: <FormattedNumber value={counts.count_livre} />, icon: MdLocalShipping, gradient: 'linear-gradient(135deg,#56AB2F,#A8E063)', delay: 40 },
                                { label: t('Retournées'), value: <FormattedNumber value={counts.count_retourne} />, icon: TbTruckReturn, gradient: 'linear-gradient(135deg,#FF9F40,#FF6384)', delay: 80 },
                                { label: t('Payées'), value: <FormattedNumber value={counts.count_paid} />, icon: MdPayments, gradient: 'linear-gradient(135deg,#43E97B,#38F9D7)', delay: 120 },
                                { label: t('Impayées'), value: <FormattedNumber value={counts.count_unpaid} />, icon: MdMoneyOff, gradient: 'linear-gradient(135deg,#FF416C,#FF4B2B)', delay: 160 },
                            ].map((c, i) => <GlassMetricCard key={i} isDark={isDark} {...c} />)}
                        </SimpleGrid>

                        {/* Status bar chart */}
                        <Box w="100%" minH={{ base: "300px", md: "400px" }} pt={6}>
                            <Bar data={statusData} options={statusOptions} />
                        </Box>
                    </Section>

                    {/* ─── RATES ─── */}
                    <Section delay={250} isDark={isDark}>
                        <SectionBadge icon={BsPercent}>{t('Taux')}</SectionBadge>
                        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 5 }} gap={3}>
                            {[
                                { label: t('Taux de confirmation'), value: tauxData?.taux_confirmation, cs: '#43E97B', ce: '#38F9D7' },
                                { label: t('Taux de livraison'), value: tauxData?.taux_delivery, cs: '#4FACFE', ce: '#00F2FE' },
                                { label: t('Taux de retour'), value: tauxData?.taux_retour, cs: '#FF9F40', ce: '#FF6384' },
                                { label: t('Taux livraison global'), value: tauxData?.taux_delivery_global, cs: '#30CFD0', ce: '#330867' },
                                { label: t('Taux retour global'), value: tauxData?.taux_retour_global, cs: '#FF416C', ce: '#FF4B2B' },
                            ].map((r, i) => (
                                <RateGauge key={i} isDark={isDark} label={r.label} value={r.value} colorStart={r.cs} colorEnd={r.ce} delay={i * 50} />
                            ))}
                        </SimpleGrid>
                    </Section>

                    {/* ─── FINANCE + CHART ─── */}
                    <Section delay={300} isDark={isDark}>
                        <SectionBadge icon={RiMoneyDollarCircleLine}>{t('Métriques financières')}</SectionBadge>
                        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 4 }} gap={3} mb={4}>
                            {[
                                { label: t("Chiffre d'affaire"), value: <FormattedNumber value={costsData?.revenue} suffix={` ${cur}`} />, sub: isDualCur ? <FormattedNumber value={costsData?.revenue_raw} suffix={` ${orderCur} × ${Number(costsData?.exchange_rate || 1).toFixed(4)}`} /> : null, icon: BsCurrencyExchange, gradient: 'linear-gradient(135deg,#6C63FF,#3D35B1)' },
                                { label: t('Profit net'), value: <FormattedNumber value={costsData?.net_profit} suffix={` ${cur}`} />, icon: (costsData?.net_profit || 0) >= 0 ? BsGraphUp : BsGraphDown, gradient: (costsData?.net_profit || 0) >= 0 ? 'linear-gradient(135deg,#43E97B,#38F9D7)' : 'linear-gradient(135deg,#FF416C,#FF4B2B)' },
                                { label: t('Marge bénéficiaire'), value: <FormattedNumber value={costsData?.profit_margin} suffix="%" />, icon: BsPercent, gradient: (costsData?.profit_margin || 0) >= 0 ? 'linear-gradient(135deg,#30CFD0,#330867)' : 'linear-gradient(135deg,#FF6B6B,#FF416C)' },
                                { label: t('AOV'), value: <FormattedNumber value={costsData?.aov} suffix={` ${cur}`} />, icon: MdAttachMoney, gradient: 'linear-gradient(135deg,#4FACFE,#00F2FE)', tooltip: t('Revenus ÷ Commandes livrées') },
                                { label: t('Bénéfice brut'), value: <FormattedNumber value={costsData?.gross_profit} suffix={` ${cur}`} />, icon: BsCashCoin, gradient: 'linear-gradient(135deg,#C471ED,#12C2E9)' },
                                { label: t('Marge bénéficiaire brute'), value: <FormattedNumber value={costsData?.gross_profit_margin} suffix="%" />, icon: MdInventory, gradient: 'linear-gradient(135deg,#C471ED,#12C2E9)' },
                                { label: t('Bénéfice par unité'), value: <FormattedNumber value={costsData?.profit_per_unit} suffix={` ${cur}`} />, icon: MdInventory, gradient: 'linear-gradient(135deg,#C471ED,#12C2E9)' },
                                { label: t('Total des charges'), value: <FormattedNumber value={chargesStats} suffix={` ${cur}`} />, icon: LuPackageX, gradient: 'linear-gradient(135deg,#F7971E,#FFD200)' },
                            ].map((c, i) => <GlassMetricCard key={i} delay={i * 45} isDark={isDark} {...c} />)}
                        </SimpleGrid>

                        {/* Revenue chart */}
                        <Box borderRadius="12px" bg={isDark ? 'rgba(0,0,0,.2)' : 'rgba(107,99,255,.04)'}
                            p={3} border={`1px solid ${borderCol}`}>
                            <Flex justify="space-between" align="center" mb={2}>
                                <Text fontWeight="700" fontSize="12px">{t("Évolution des Revenus et des Charges")}</Text>
                                {!dateStart && (
                                    <Select size="xs" w="90px" value={year} borderRadius="6px"
                                        onChange={e => setYear(e.target.value === '0' ? currentYear : e.target.value)}>
                                        <option value="0">{t('années')}</option>
                                        {Array.from({ length: currentYear - 2023 }, (_, i) => 2024 + i).map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </Select>
                                )}
                            </Flex>
                            <Box h="220px">
                                {lineChartData && lineChartData.length > 0 ? (
                                    <LineChart lineChartData={lineChartData} lineChartOptions={lineOptions} />
                                ) : (
                                    <Box display="flex" h="100%" alignItems="center" justifyContent="center" color="gray.400">
                                        <Text fontSize="12px">{t('Aucune donnée de revenu ou de charge disponible')}</Text>
                                    </Box>
                                )}
                            </Box>
                            {/* Legend */}
                            {lineChartData && lineChartData.length > 0 && (
                                <Flex gap={4} mt={1} justify="center">
                                    {[{ color: '#6C63FF', label: t('Revenus') }, { color: '#FF6584', label: t('Charges') }].map(l => (
                                        <Flex key={l.label} align="center" gap={1.5}>
                                            <Box w="8px" h="8px" borderRadius="50%" bg={l.color} />
                                            <Text fontSize="10px" fontWeight="600" color="gray.500">{l.label}</Text>
                                        </Flex>
                                    ))}
                                </Flex>
                            )}
                        </Box>
                    </Section>

                    {/* ─── ADVERTISING ─── */}
                    <Section delay={350} isDark={isDark}>
                        <SectionBadge icon={RiAdvertisementLine}>{t('Métriques publicitaires')}</SectionBadge>
                        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 4 }} gap={3}>
                            {[
                                { label: t('Dépense publicitaire'), value: <FormattedNumber value={costsData?.total_ad_spend} suffix={` ${cur}`} />, icon: RiAdvertisementLine, gradient: 'linear-gradient(135deg,#FA709A,#FEE140)' },
                                { label: t('Coût par prospect'), value: <FormattedNumber value={costsData?.cost_per_lead} />, icon: MdOutlineRemoveShoppingCart, gradient: 'linear-gradient(135deg,#F7971E,#FFD200)' },
                                { label: t('CPL Confirmé'), value: <FormattedNumber value={costsData?.cost_per_confirmed_lead} />, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#43E97B,#38F9D7)' },
                                { label: t('CPL Livré'), value: <FormattedNumber value={costsData?.cost_per_delivered_lead} />, icon: TbTruckDelivery, gradient: 'linear-gradient(135deg,#4FACFE,#00F2FE)' },
                                { label: t('ROAS'), value: <FormattedNumber value={costsData?.roas} />, icon: BsGraphUp, gradient: 'linear-gradient(135deg,#6C63FF,#3D35B1)' },
                                { label: t('ROI'), value: <FormattedNumber value={costsData?.roi} suffix="%" />, icon: MdTrendingUp, gradient: 'linear-gradient(135deg,#30CFD0,#330867)' },
                                { label: t('Break-even CPL'), value: costsData?.breakeven_cpl, icon: MdHourglassEmpty, gradient: 'linear-gradient(135deg,#C471ED,#12C2E9)' },
                            ].map((c, i) => <GlassMetricCard key={i} delay={i * 45} isDark={isDark} {...c} />)}
                        </SimpleGrid>
                    </Section>

                    {/* ─── EXPENSES CHART ─── */}
                    <Section delay={400} isDark={isDark}>
                        <SectionBadge icon={MdOutlineBarChart}>{t('Statistiques des charges')}</SectionBadge>
                        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
                            <Box>
                                <Text fontSize="11px" fontWeight="700" color="gray.400" mb={2} textTransform="uppercase" letterSpacing="1px">
                                    {t('Répartition')}
                                </Text>
                                <Box h="220px" display="flex" alignItems="center" justifyContent="center" position="relative">
                                    {hasCharges
                                        ? (
                                            <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
                                                <Box position="relative" w="180px" h="180px">
                                                    <Doughnut data={donutData} options={donutOptions} />
                                                    <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" textAlign="center" pointerEvents="auto" zIndex={10}>
                                                        <ChakraTooltip label={`${totalCharges} ${cur}`} hasArrow placement="top">
                                                            <Box cursor="pointer">
                                                                <Text fontSize="10px" fontWeight="bold" color={isDark ? '#718096' : '#A0AEC0'}>{t('TOTAL')}</Text>
                                                                <Text fontSize="13px" fontWeight="bold" color={isDark ? '#E2E8F0' : '#2D3748'}>
                                                                    {formatNumber(totalCharges)} {cur}
                                                                </Text>
                                                            </Box>
                                                        </ChakraTooltip>
                                                    </Box>
                                                </Box>
                                                <Flex direction="column" justify="center" ml={6} maxH="200px" overflowY="auto" pr={2}>
                                                    {filteredLabels.map((label, idx) => (
                                                        <Flex key={idx} align="center" mb={1.5}>
                                                            <Box w="8px" h="8px" borderRadius="full" bg={filteredColors[idx]} mr={2} flexShrink={0} />
                                                            <Text fontSize="11px" color={isDark ? '#CBD5E0' : '#4A5568'} fontFamily="Inter">
                                                                {label}
                                                            </Text>
                                                        </Flex>
                                                    ))}
                                                </Flex>
                                            </Flex>
                                        )
                                        : <Box textAlign="center" color="gray.400">
                                            <MdOutlineBarChart size={36} style={{ margin: '0 auto 6px' }} />
                                            <Text fontSize="11px">{t('Aucune donnée de charge disponible')}</Text>
                                        </Box>}
                                </Box>
                            </Box>
                            <Box>
                                <Text fontSize="11px" fontWeight="700" color="gray.400" mb={2} textTransform="uppercase" letterSpacing="1px">
                                    {t('Coûts')}
                                </Text>
                                <Box h="220px">
                                    {hasCharges
                                        ? <Bar data={expBarData} options={expBarOptions} />
                                        : <Box display="flex" h="100%" alignItems="center" justifyContent="center" color="gray.400">
                                            <Text fontSize="11px">{t('Les charges apparaîtront ici une fois ajoutées')}</Text>
                                        </Box>}
                                </Box>
                            </Box>
                        </Grid>
                    </Section>

                    {/* ─── BREAKDOWN TABS ─── */}
                    <Section delay={450} isDark={isDark}>
                        <SectionBadge icon={MdInventory}>{t('Répartition')}</SectionBadge>
                        <Flex gap={2} mb={3} flexWrap="wrap">
                            {[
                                t('Commandes par produit'),
                                t('Commandes par source'),
                                // t('Commandes par service')
                            ].map((tab, i) => (
                                <Box
                                    key={i}
                                    as="button"
                                    onClick={() => setBreakdownTab(i)}
                                    px={3} py={1.5} borderRadius="10px" fontSize="11px" fontWeight="700"
                                    transition="all .2s"
                                    bg={breakdownTab === i
                                        ? 'linear-gradient(135deg,#7B5EA7,#5A3B8A)'
                                        : isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.05)'}
                                    color={breakdownTab === i ? 'white' : isDark ? 'whiteAlpha.700' : 'gray.600'}
                                >
                                    {tab}
                                </Box>
                            ))}
                        </Flex>

                        <TableContainer maxH="280px" overflowY="auto"
                            borderRadius="12px" border={`1px solid ${borderCol}`}>
                            <Table size="sm" variant="unstyled">
                                <Thead bg={isDark ? 'rgba(0,0,0,.3)' : 'rgba(107,99,255,.05)'} position="sticky" top={0} zIndex={1}>
                                    {breakdownTab === 0 && <Tr>
                                        <Th py={2} fontSize="9px" textTransform="uppercase" letterSpacing="1px" color="gray.400">{t('Produit')}</Th>
                                        <Th isNumeric fontSize="9px" color="gray.400">{t('Total')}</Th>
                                        <Th isNumeric fontSize="9px" color="gray.400">{t('Confirmées')}</Th>
                                        <Th isNumeric fontSize="9px" color="gray.400">{t('Livrées')}</Th>
                                    </Tr>}
                                    {breakdownTab === 1 && <Tr>
                                        <Th py={2} fontSize="9px" textTransform="uppercase" letterSpacing="1px" color="gray.400">{t('Source')}</Th>
                                        <Th isNumeric fontSize="9px" color="gray.400">{t('Total')}</Th>
                                    </Tr>}
                                    {breakdownTab === 2 && <Tr>
                                        <Th py={2} fontSize="9px" textTransform="uppercase" letterSpacing="1px" color="gray.400">{t('Service')}</Th>
                                        <Th isNumeric fontSize="9px" color="gray.400">{t('Total')}</Th>
                                    </Tr>}
                                </Thead>
                                <Tbody>
                                    {breakdownTab === 0 && (breakdownData?.by_product || []).map(r => (
                                        <Tr key={r.product_id} _hover={{ bg: isDark ? 'whiteAlpha.50' : 'purple.50' }} transition="all .1s">
                                            <Td py={2} fontWeight="600" fontSize="12px" maxW="200px" isTruncated>{r.product_title}</Td>
                                            <Td isNumeric py={2}><Box display="inline-block" px={2} py={0.5} borderRadius="6px" bg="rgba(76,172,254,.15)" color="#4FACFE" fontSize="11px" fontWeight="700"><FormattedNumber value={r.total} /></Box></Td>
                                            <Td isNumeric py={2}><Box display="inline-block" px={2} py={0.5} borderRadius="6px" bg="rgba(67,233,123,.15)" color="#43E97B" fontSize="11px" fontWeight="700"><FormattedNumber value={r.confirmed} /></Box></Td>
                                            <Td isNumeric py={2}><Box display="inline-block" px={2} py={0.5} borderRadius="6px" bg="rgba(108,99,255,.15)" color="#6C63FF" fontSize="11px" fontWeight="700"><FormattedNumber value={r.delivered} /></Box></Td>
                                        </Tr>
                                    ))}
                                    {breakdownTab === 1 && (breakdownData?.by_source || []).map((r, i) => (
                                        <Tr key={i} _hover={{ bg: isDark ? 'whiteAlpha.50' : 'purple.50' }} transition="all .1s">
                                            <Td py={2} fontWeight="600" fontSize="12px">{r.source || t('Manuel')}</Td>
                                            <Td isNumeric py={2}><Box display="inline-block" px={2} py={0.5} borderRadius="6px" bg="rgba(108,99,255,.15)" color="#6C63FF" fontSize="11px" fontWeight="700"><FormattedNumber value={r.total} /></Box></Td>
                                        </Tr>
                                    ))}
                                    {breakdownTab === 2 && (breakdownData?.by_service || []).map((r, i) => (
                                        <Tr key={i} _hover={{ bg: isDark ? 'whiteAlpha.50' : 'purple.50' }} transition="all .1s">
                                            <Td py={2} fontWeight="600" fontSize="12px">{r.service}</Td>
                                            <Td isNumeric py={2}><Box display="inline-block" px={2} py={0.5} borderRadius="6px" bg="rgba(48,207,208,.15)" color="#30CFD0" fontSize="11px" fontWeight="700"><FormattedNumber value={r.total} /></Box></Td>
                                        </Tr>
                                    ))}
                                    {((breakdownTab === 0 && !breakdownData?.by_product?.length) ||
                                        (breakdownTab === 1 && !breakdownData?.by_source?.length) ||
                                        (breakdownTab === 2 && !breakdownData?.by_service?.length)) && (
                                            <Tr><Td colSpan={4} textAlign="center" py={6} color="gray.400" fontSize="11px">{t('Aucune donnée')}</Td></Tr>
                                        )}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </Section>

                    {/* ─── RANKINGS ─── */}
                    <Section delay={500} isDark={isDark}>
                        <SectionBadge icon={BsGraphUp}>{t('Classement des produits')}</SectionBadge>
                        <Flex gap={2} mb={3} flexWrap="wrap">
                            {[t('Meilleures ventes'), t('Profit le plus élevé'), t('Meilleur profit/unité'), t('Taux de livraison le plus élevé'), t('Taux de retour le plus élevé')].map((tab, i) => (
                                <Box
                                    key={i} as="button" onClick={() => setRankingTab(i)}
                                    px={2.5} py={1} borderRadius="8px" fontSize="11px" fontWeight="700"
                                    transition="all .2s"
                                    bg={rankingTab === i
                                        ? 'linear-gradient(135deg,#7B5EA7,#5A3B8A)'
                                        : isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.05)'}
                                    color={rankingTab === i ? 'white' : isDark ? 'whiteAlpha.700' : 'gray.600'}
                                >
                                    {tab}
                                </Box>
                            ))}
                        </Flex>

                        <Box borderRadius="12px" border={`1px solid ${borderCol}`} overflow="hidden">
                            {rankingTab === 0 && <RankTable isDark={isDark} data={rankingsData?.best_selling} cols={[
                                { key: 'total_orders', label: t('Total') },
                                { key: 'delivered', label: t('Livrées'), color: v => v > 0 ? '#43E97B' : 'gray.400' },
                            ]} />}
                            {rankingTab === 1 && <RankTable isDark={isDark} data={rankingsData?.highest_profit} cols={[
                                { key: 'revenue', label: t('CA'), format: v => <FormattedNumber value={v} suffix={` ${cur}`} /> },
                                { key: 'net_profit', label: t('Profit'), format: v => <FormattedNumber value={v} suffix={` ${cur}`} />, color: v => v >= 0 ? '#43E97B' : '#FF6384' },
                            ]} />}
                            {rankingTab === 2 && <RankTable isDark={isDark} data={rankingsData?.highest_profit_per_unit} cols={[
                                { key: 'delivered', label: t('Livrées') },
                                { key: 'profit_per_unit', label: t('Profit/unité'), format: v => <FormattedNumber value={v} suffix={` ${cur}`} />, color: v => v >= 0 ? '#43E97B' : '#FF6384' },
                            ]} />}
                            {rankingTab === 3 && <RankTable isDark={isDark} data={rankingsData?.most_delivered} cols={[
                                { key: 'confirmed', label: t('Confirmées') },
                                { key: 'delivery_rate', label: t('Taux liv.'), format: v => <FormattedNumber value={v} suffix="%" />, color: v => v >= 60 ? '#43E97B' : '#FF9F40' },
                            ]} />}
                            {rankingTab === 4 && <RankTable isDark={isDark} data={rankingsData?.most_returned} cols={[
                                { key: 'confirmed', label: t('Confirmées') },
                                { key: 'return_rate', label: t('Taux ret.'), format: v => <FormattedNumber value={v} suffix="%" />, color: v => v > 20 ? '#FF6384' : '#43E97B' },
                            ]} />}
                        </Box>
                    </Section>

                    {/* ─── PERSONNEL ─── */}
                    {users.length > 0 && (
                        <Section delay={550} isDark={isDark}>
                            <SectionBadge icon={MdCheckCircle}>{t('Liste des personnels')}</SectionBadge>
                            <SimpleGrid columns={{ base: 3, sm: 4, md: 5, lg: 8 }} gap={2} mb={3}>
                                {users.map((u, i) => (
                                    <Box
                                        key={i}
                                        className="product-card"
                                        borderRadius="12px"
                                        border={`2px solid ${selectedUser?.id === u.id ? '#7B5EA7' : borderCol}`}
                                        bg={selectedUser?.id === u.id
                                            ? isDark ? 'rgba(123,94,167,.2)' : 'rgba(123,94,167,.07)'
                                            : isDark ? 'rgba(255,255,255,.04)' : 'rgba(255,255,255,.9)'}
                                        p={2} textAlign="center"
                                        onClick={() => {
                                            if (selectedUser?.id === u.id) { setSelectedUser(null); setUserStatsOpen(false); return; }
                                            setSelectedUser(u); setUserStatsOpen(true);
                                            const ur = user?.role || user?.role_client;
                                            if (u.role_client === 'RespoSuiviClient') {
                                                axiosClient.get(`/commandes/getStatsSuivi/?client=${user.id}&suivi=${u.id}&dateStart=${dateStart}&dateEnd=${dateEnd}&userRole=${ur}&business=${currentBusiness?.id}`)
                                                    .then(r => setUserStatsSuivi(r.data)).catch(() => { });
                                            } else if (u.role_client === 'StockManagerClient') {
                                                axiosClient.get(`/commandes/getStatsStock/?client=${user.id}&stockManager=${u.id}&dateStart=${dateStart}&dateEnd=${dateEnd}&userRole=${ur}&business=${currentBusiness?.id}`)
                                                    .then(r => setUserStatsStock(r.data)).catch(() => { });
                                            }
                                        }}
                                    >
                                        <Avatar src={u?.image} size="sm" mb={1} mx="auto" />
                                        <Text fontSize="10px" fontWeight="700" noOfLines={1}>{u.first_name} {u.last_name}</Text>
                                        <Box display="inline-block" mt={0.5} px={1.5} py={0} borderRadius="4px"
                                            bg="linear-gradient(135deg,rgba(123,94,167,.3),rgba(90,59,138,.3))">
                                            <Text fontSize="8px" fontWeight="700" color="purple.300">{u.role_client}</Text>
                                        </Box>
                                    </Box>
                                ))}
                            </SimpleGrid>
                            <Collapse in={userStatsOpen}>
                                <Box borderRadius="12px" border={`1px solid ${borderCol}`} p={3} bg={isDark ? 'rgba(0,0,0,.2)' : 'rgba(107,99,255,.03)'}>
                                    <Flex justify="flex-end" mb={2}>
                                        <CloseButton size="sm" onClick={() => { setUserStatsOpen(false); setSelectedUser(null); }} />
                                    </Flex>
                                    {selectedUser?.role_client === 'RespoSuiviClient' ? (
                                        <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                                            {[
                                                { label: t('Nombre de commandes en cours'), value: <FormattedNumber value={userStatsSuivi?.num_commandes_en_cours} />, icon: MdHourglassEmpty, gradient: 'linear-gradient(135deg,#F7971E,#FFD200)' },
                                                { label: t('Nombre de commandes confirmés'), value: <FormattedNumber value={userStatsSuivi?.num_commandes_confirmes} />, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#43E97B,#38F9D7)' },
                                                { label: t('Nombre de commandes livrés'), value: <FormattedNumber value={userStatsSuivi?.num_commandes_livres} />, icon: TbTruckDelivery, gradient: 'linear-gradient(135deg,#4FACFE,#00F2FE)' },
                                                { label: t('Nombre de commandes annulés'), value: <FormattedNumber value={userStatsSuivi?.num_commandes_annules} />, icon: MdOutlineCancel, gradient: 'linear-gradient(135deg,#FF416C,#FF4B2B)' },
                                            ].map((c, i) => <GlassMetricCard key={i} isDark={isDark} {...c} />)}
                                        </SimpleGrid>
                                    ) : selectedUser?.role_client === 'StockManagerClient' ? (
                                        <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                                            {[
                                                { label: t('Nombre de commandes en cours'), value: <FormattedNumber value={userStatsStock?.num_commandes_en_cours} />, icon: MdHourglassEmpty, gradient: 'linear-gradient(135deg,#F7971E,#FFD200)' },
                                                { label: t('Nombre de commandes Emballés'), value: <FormattedNumber value={userStatsStock?.num_commandes_embales} />, icon: LuPackageCheck, gradient: 'linear-gradient(135deg,#30CFD0,#330867)' },
                                                { label: t('Nombre de commandes Collectés'), value: <FormattedNumber value={userStatsStock?.num_commandes_collecte} />, icon: LuPackage, gradient: 'linear-gradient(135deg,#4FACFE,#00F2FE)' },
                                                { label: t('Nombre de commandes annulés'), value: <FormattedNumber value={userStatsStock?.num_commandes_annules} />, icon: MdOutlineCancel, gradient: 'linear-gradient(135deg,#FF416C,#FF4B2B)' },
                                            ].map((c, i) => <GlassMetricCard key={i} isDark={isDark} {...c} />)}
                                        </SimpleGrid>
                                    ) : null}
                                </Box>
                            </Collapse>
                        </Section>
                    )}

                </Box>
            </Box>
        </>
    );
}

export default Statistics;
